import { useState, useEffect, useRef } from 'react'
import { capstoneService } from '../../services'
import toast from 'react-hot-toast'
import {
  PlayIcon,
  PauseIcon,
  ClockIcon,
} from '@heroicons/react/24/outline'

export default function TimeTracker({ capstoneId }) {
  const [isTracking, setIsTracking] = useState(false)
  const [elapsedSeconds, setElapsedSeconds] = useState(0)
  const [totalTime, setTotalTime] = useState(null)
  const [loading, setLoading] = useState(true)
  const intervalRef = useRef(null)
  const lastActivityRef = useRef(Date.now())
  const sessionStartRef = useRef(null)

  // Load initial time data
  useEffect(() => {
    loadTimeData()
  }, [capstoneId])

  // Timer interval
  useEffect(() => {
    if (isTracking) {
      intervalRef.current = setInterval(() => {
        const now = Date.now()
        const secondsSinceStart = Math.floor((now - sessionStartRef.current) / 1000)
        setElapsedSeconds(secondsSinceStart)

        // Check for idle timeout (30 minutes)
        const idleTime = now - lastActivityRef.current
        const idleMinutes = idleTime / 1000 / 60
        
        if (idleMinutes >= 30) {
          handleStop(true) // Auto-stop due to timeout
        }
      }, 1000)
    } else {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
        intervalRef.current = null
      }
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
      }
    }
  }, [isTracking])

  // Track user activity
  useEffect(() => {
    const updateActivity = () => {
      lastActivityRef.current = Date.now()
    }

    if (isTracking) {
      window.addEventListener('mousemove', updateActivity)
      window.addEventListener('keypress', updateActivity)
      window.addEventListener('click', updateActivity)
      window.addEventListener('scroll', updateActivity)

      return () => {
        window.removeEventListener('mousemove', updateActivity)
        window.removeEventListener('keypress', updateActivity)
        window.removeEventListener('click', updateActivity)
        window.removeEventListener('scroll', updateActivity)
      }
    }
  }, [isTracking])

  const loadTimeData = async () => {
    try {
      setLoading(true)
      const data = await capstoneService.getTimeData(capstoneId)
      
      if (data.active_session) {
        setIsTracking(true)
        const startTime = new Date(data.active_session.started_at).getTime()
        sessionStartRef.current = startTime
        const elapsed = Math.floor((Date.now() - startTime) / 1000)
        setElapsedSeconds(elapsed)
      }
      
      setTotalTime(data.total_time)
    } catch (err) {
      console.error('Failed to load time data:', err)
    } finally {
      setLoading(false)
    }
  }

  const handleStart = async () => {
    try {
      const response = await capstoneService.startTimer(capstoneId)
      setIsTracking(true)
      sessionStartRef.current = new Date(response.session.started_at).getTime()
      setElapsedSeconds(0)
      lastActivityRef.current = Date.now()
      toast.success('Time tracking started')
    } catch (err) {
      console.error('Failed to start timer:', err)
      toast.error('Failed to start timer')
    }
  }

  const handleStop = async (isTimeout = false) => {
    try {
      await capstoneService.stopTimer(capstoneId)
      setIsTracking(false)
      setElapsedSeconds(0)
      sessionStartRef.current = null
      
      if (isTimeout) {
        toast('Session auto-paused due to inactivity', { icon: '⏸️' })
      } else {
        toast.success('Time tracking stopped')
      }
      
      // Reload time data to get updated totals
      await loadTimeData()
    } catch (err) {
      console.error('Failed to stop timer:', err)
      if (!isTimeout) {
        toast.error('Failed to stop timer')
      }
    }
  }

  const formatTime = (seconds) => {
    const hrs = Math.floor(seconds / 3600)
    const mins = Math.floor((seconds % 3600) / 60)
    const secs = seconds % 60

    if (hrs > 0) {
      return `${hrs}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
    }
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="animate-pulse flex items-center">
          <div className="h-10 w-10 bg-gray-200 rounded-full mr-3"></div>
          <div className="h-6 bg-gray-200 rounded w-32"></div>
        </div>
      </div>
    )
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          {/* Play/Pause Button */}
          <button
            onClick={isTracking ? () => handleStop(false) : handleStart}
            className={`p-3 rounded-full transition-all ${
              isTracking
                ? 'bg-red-500 hover:bg-red-600 text-white'
                : 'bg-cte-blue hover:bg-cte-blue/90 text-white'
            }`}
            title={isTracking ? 'Stop tracking' : 'Start tracking'}
          >
            {isTracking ? (
              <PauseIcon className="w-5 h-5" />
            ) : (
              <PlayIcon className="w-5 h-5" />
            )}
          </button>

          {/* Timer Display */}
          <div>
            <div className="flex items-center space-x-2">
              <ClockIcon className="w-5 h-5 text-gray-400" />
              <span className={`text-2xl font-mono font-bold ${
                isTracking ? 'text-cte-blue' : 'text-gray-700'
              }`}>
                {formatTime(elapsedSeconds)}
              </span>
            </div>
            {isTracking && (
              <p className="text-xs text-gray-500 mt-1">
                Tracking time...
              </p>
            )}
          </div>
        </div>

        {/* Total Time */}
        {totalTime && totalTime.minutes > 0 && (
          <div className="text-right">
            <p className="text-xs text-gray-500 uppercase tracking-wide">Total Time</p>
            <p className="text-lg font-semibold text-gray-700">
              {totalTime.formatted}
            </p>
          </div>
        )}
      </div>

      {/* Info Message */}
      {!isTracking && totalTime && totalTime.minutes === 0 && (
        <p className="text-sm text-gray-500 mt-3 flex items-center">
          <ClockIcon className="w-4 h-4 mr-1" />
          Click the play button to start tracking your work time
        </p>
      )}
    </div>
  )
}
